import { useState } from "react";

// Semáforo de cobertura mensual: para un contrato y una lista de documentos
// guardados con periodo [desde, hasta] (ejercicios sombra, facturas…), pinta
// los 12 meses del año elegido:
//   verde    = todos los días del mes (dentro de la vigencia) están cubiertos
//   amarillo = hay al menos un día capturado pero faltan (muestra avance N/M)
//   rojo     = no se ha capturado NADA del mes
//   gris     = el mes no aplica (fuera de la vigencia del contrato)
// El año elegido se recuerda entre visitas (localStorage, una clave por página).

const MESES_CORTOS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function estadoMes(
  anio: number,
  mes0: number, // 0-11
  vigIni: string | null | undefined,
  vigFin: string | null | undefined,
  periodos: { desde: string; hasta: string }[],
): { estado: "ok" | "parcial" | "falta" | "na"; cubiertos: number; total: number } {
  const nDias = new Date(anio, mes0 + 1, 0).getDate();
  let total = 0;
  let cubiertos = 0;
  for (let d = 1; d <= nDias; d++) {
    const iso = `${anio}-${String(mes0 + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    if (vigIni && iso < vigIni) continue;
    if (vigFin && iso > vigFin) continue;
    total++;
    if (periodos.some((e) => e.desde <= iso && iso <= e.hasta)) cubiertos++;
  }
  const estado =
    total === 0 ? "na" : cubiertos === total ? "ok" : cubiertos > 0 ? "parcial" : "falta";
  return { estado, cubiertos, total };
}

export function CoberturaMeses({
  vigIni,
  vigFin,
  periodos,
  storageKey,
  docLabel,
  tipos,
  pendientes,
  mesesCalendario,
}: {
  vigIni: string | null | undefined;
  vigFin: string | null | undefined;
  periodos: { desde: string; hasta: string }[];
  /** clave de localStorage donde se recuerda el año elegido (una por página) */
  storageKey: string;
  /** cómo se llama el documento que cubre días: "ejercicios sombra", "facturas guardadas"… */
  docLabel: string;
  /** MODO PRELIMINAR/AJUSTE (contratos prepago_gas): mapa "AAAA-MM" → tipo de la
   *  factura del mes. Cambia la semántica del semáforo: rojo = nada, amarillo =
   *  solo preliminar, verde = preliminar + ajuste (o factura completa). */
  tipos?: Record<string, string> | null;
  /** meses "AAAA-MM" con cálculo PENDIENTE (TBFin liquidada sin documento):
   *  se marcan con una bolita roja además del color del semáforo */
  pendientes?: string[] | null;
  /** MODO PARCIALIDADES (potencia): solo estos meses "AAAA-MM" forman parte del
   *  calendario de pagos — los demás salen grises aunque estén en vigencia.
   *  Un mes del calendario es verde si hay documento que lo cubra y rojo si no
   *  (no hay "parcial": la parcialidad es una por mes). */
  mesesCalendario?: string[] | null;
}) {
  const [anio, setAnio] = useState<number>(() => {
    const g = Number(localStorage.getItem(storageKey));
    return Number.isFinite(g) && g >= 2000 && g <= 2100 ? g : new Date().getFullYear();
  });
  const cambiar = (a: number) => {
    setAnio(a);
    localStorage.setItem(storageKey, String(a));
  };

  // Años ofrecidos: los de la vigencia del contrato + el año en curso + el guardado.
  const anios = (() => {
    const s = new Set<number>([new Date().getFullYear(), anio]);
    if (vigIni) {
      const ini = Number(vigIni.slice(0, 4));
      const fin = Number((vigFin ?? vigIni).slice(0, 4));
      for (let a = ini; a <= fin; a++) s.add(a);
    }
    return [...s].sort();
  })();
  const meses = MESES_CORTOS.map((nombre, i) => {
    const base = { nombre, ...estadoMes(anio, i, vigIni, vigFin, periodos), tipo: "" };
    // Modo parcialidades (potencia): solo cuentan los meses DEL CALENDARIO; en
    // ellos, verde = hay documento que cubre el mes y rojo = falta.
    if (mesesCalendario) {
      const ym = `${anio}-${String(i + 1).padStart(2, "0")}`;
      if (!mesesCalendario.includes(ym)) {
        base.estado = "na";
      } else {
        const dia1 = `${ym}-01`;
        base.estado = periodos.some((e) => e.desde <= dia1 && dia1 <= e.hasta)
          ? "ok" : "falta";
      }
    }
    // Modo preliminar/ajuste: el estado lo decide el TIPO de la factura del
    // mes, no la cobertura por días (la preliminar cubre el mes completo pero
    // sigue pendiente de ajustarse).
    if (tipos && base.estado !== "na") {
      const t = tipos[`${anio}-${String(i + 1).padStart(2, "0")}`] ?? "";
      base.tipo = t;
      base.estado = t === "ajuste" || t === "completa" ? "ok"
        : t === "preliminar" ? "parcial" : "falta";
    }
    return {
      ...base,
      pendiente: (pendientes ?? []).includes(`${anio}-${String(i + 1).padStart(2, "0")}`),
    };
  });

  return (
    <div className="w-full border-t pt-3 mt-1 flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-slate-600">Cobertura</span>
      <select
        className="border rounded px-1.5 py-1 text-xs"
        value={anio}
        onChange={(e) => cambiar(Number(e.target.value))}
        title="Año del calendario de cobertura (se recuerda al volver)"
      >
        {anios.map((a) => (
          <option key={a} value={a}>{a}</option>
        ))}
      </select>
      <div className="flex flex-wrap gap-1.5">
        {meses.map((m) => (
          <span
            key={m.nombre}
            title={
              m.estado === "na"
                ? (mesesCalendario
                    ? `${m.nombre} ${anio}: sin parcialidad en este mes`
                    : `${m.nombre} ${anio}: fuera de la vigencia del contrato`)
                : mesesCalendario
                  ? m.estado === "ok"
                    ? `${m.nombre} ${anio}: parcialidad guardada en ${docLabel}`
                    : `${m.nombre} ${anio}: parcialidad SIN guardar en ${docLabel}`
                  : tipos
                  ? m.estado === "ok"
                    ? `${m.nombre} ${anio}: ${m.tipo === "completa" ? "factura completa guardada" : "preliminar y ajuste guardados"}`
                    : m.estado === "parcial"
                      ? `${m.nombre} ${anio}: solo la preliminar guardada — falta el ajuste del mes operado`
                      : `${m.nombre} ${anio}: sin factura preliminar`
                  : m.estado === "ok"
                    ? `${m.nombre} ${anio}: los ${m.total} días ya están en ${docLabel}`
                    : m.estado === "parcial"
                      ? `${m.nombre} ${anio}: ${m.cubiertos} de ${m.total} días capturados — faltan ${m.total - m.cubiertos}`
                      : `${m.nombre} ${anio}: sin ${docLabel} (0 de ${m.total} días)`
            }
            className={`text-[11px] font-medium rounded px-2 py-1 select-none ${
              m.estado === "ok"
                ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                : m.estado === "parcial"
                  ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                  : m.estado === "falta"
                    ? "bg-red-100 text-red-700 border border-red-300"
                    : "bg-slate-100 text-slate-400 border border-slate-200"
            }`}
          >
            {m.nombre}
            {m.estado === "parcial" && (
              <span className="ml-1 text-[10px] opacity-70">
                {tipos ? "prel" : `${m.cubiertos}/${m.total}`}
              </span>
            )}
            {m.pendiente && (
              <span
                className="inline-block w-2 h-2 rounded-full bg-red-500 ml-1 align-middle ring-1 ring-white"
                title={`${m.nombre} ${anio}: hay TBFin liquidada en el ETRM sin ${docLabel} — captúralo aquí`}
              />
            )}
          </span>
        ))}
      </div>
      <span className="text-[10px] text-slate-400">
        {mesesCalendario
          ? "verde = parcialidad guardada · rojo = parcialidad sin guardar · gris = mes sin parcialidad"
          : tipos
            ? "verde = preliminar + ajuste · amarillo = solo preliminar · rojo = sin capturar · gris = no aplica"
            : "verde = mes cubierto · amarillo = parcial · rojo = sin capturar · gris = no aplica"}
      </span>
    </div>
  );
}
